import { useRef } from "react";
import { useGSAP } from "@gsap/react";
import { gsap } from "gsap";
import Image from "next/image";
import { cn } from "@/app/utils/className";
import { useRouter } from "next/navigation";
import { ButtonLink } from "../button-link";
import { Heading2, Heading3 } from "../typography";
import {
  GridSolutionCard,
  realWorldSolutionsCards,
} from "../case-studies/case-studies-data";

interface SolutionCardProps {
  solution: GridSolutionCard;
}

interface StatsProps {
  solution: GridSolutionCard;
  className?: string;
}

// Individual card component
const SolutionCard = ({ solution }: SolutionCardProps) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  useGSAP(() => {
    const mm = gsap.matchMedia();

    mm.add("(min-width: 768px)", () => {
      const card = cardRef.current;
      if (!card) return;

      // Set initial state
      gsap.set(card, {
        opacity: 0,
        y: 60,
        scale: 0.95,
      });

      // Animate on scroll
      gsap.to(card, {
        opacity: 1,
        y: 0,
        scale: 1,
        duration: 0.8,
        ease: "back.out(1.7)",
        scrollTrigger: {
          trigger: card,
          start: "top 85%",
          toggleActions: "play none none reverse",
        },
      });
    });
  }, []);

  const isTwoColumn = solution.className.includes("col-span-2");

  return (
    <div
      ref={cardRef}
      className={cn(
        "group relative rounded-2xl overflow-hidden cursor-pointer @container/solution-card",
        solution.className
      )}
      onClick={() => {
        if (solution.link) {
          router.push(solution.link);
        }
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter" && solution.link) {
          router.push(solution.link);
        }
      }}
      role="button"
      aria-label={`Learn more about ${solution.title}`}
      tabIndex={0}
    >
      {/* Background Image Layer */}
      <div className="absolute inset-0">
        <Image
          src={solution.image}
          alt={solution.title}
          width={800}
          height={600}
          className="size-full object-cover transition-transform duration-700 group-hover:scale-110"
          draggable={false}
        />
        {/* Gradient Overlays - matching other sections */}
        <div className="absolute inset-0 bg-gradient-to-b from-black via-30% via-black/30 group-hover:via-black/70 to-black"></div>
        <div
          className={`absolute inset-0 bg-gradient-to-br ${solution.bgColor} opacity-40`}
        ></div>
      </div>

      <div className="relative size-full bg-white/5 hover:bg-black/5 border border-white/10 hover:border-white/20 transition-all duration-500 group-hover:scale-[1.01]">
        {/* Gradient glow effect on hover */}
        <div
          className={`absolute inset-0 bg-gradient-to-br ${solution.bgColor.replace(
            "/60",
            "/20"
          )} opacity-0 group-hover:opacity-30 transition-opacity duration-500 blur-xl`}
        ></div>

        <div className="relative z-10 size-full flex flex-col justify-between p-6">
          {/* Header Section */}
          <div>
            <h4 className="text-xl @lg/solution-card:text-2xl font-bold mb-2 text-white transition-colors duration-300">
              {solution.title}
            </h4>
            <p className="text-slate-100 group-hover:text-slate-50 text-sm @lg/solution-card:text-base mb-4 line-clamp-1">
              {solution.subtitle}
            </p>
          </div>

          {/* Stats Section */}
          <div className="space-y-4 pb-6">
            <Stats
              key={solution.id}
              solution={solution}
              className={cn({
                "md:hidden": !isTwoColumn,
              })}
            />

            {/* Expandable Description */}
            <div className="lg:max-h-0 overflow-hidden group-hover:max-h-96 transition-all duration-500 ease-out">
              <div
                className={cn("overflow-auto scrollbar-hidden pb-6", {
                  "lg:h-40 xl:h-44": !solution.className.includes("col-span-2"),
                })}
              >
                <Stats
                  key={solution.id}
                  solution={solution}
                  className={cn("hidden md:grid mb-4", {
                    "md:hidden": isTwoColumn,
                  })}
                />

                <p
                  className={cn(
                    "text-base text-slate-200 leading-relaxed line-clamp-1  md:line-clamp-2 @lg/solution-card:text-lg"
                  )}
                >
                  {solution.description}
                </p>
              </div>
            </div>
          </div>
          {/* Call to Action */}
          <div className="absolute left-6 bottom-6 flex items-center gap-2 text-white transition-colors duration-300 pt-2">
            <span className="text-sm font-semibold">Learn more</span>
            <Image
              src="/images/arrow-right.svg"
              alt="Arrow Right"
              width={20}
              height={20}
              className="invert opacity-75 group-hover:opacity-100 animate-[movehorizontal_1.5s_infinite_alternate] h-auto"
            />
          </div>
        </div>

        {/* Hover accent line - matching other sections */}
        <div className="absolute bottom-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>

        {/* Corner accents - matching other sections */}
        <div className="absolute top-4 right-4 w-8 h-8 border-t-2 border-r-2 border-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
        <div className="absolute bottom-4 left-4 w-8 h-8 border-b-2 border-l-2 border-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
      </div>
    </div>
  );
};

export const RealWorldSolutions = ({
  largeTitle,
}: {
  largeTitle?: boolean;
}) => {
  return (
    <div
      id="real-world-solutions"
      className="real-world-solutions py-20 text-white relative overflow-hidden"
    >
      <div className="container relative z-10">
        {largeTitle ? (
          <Heading2 className="lg:mb-12">Explore Our Case Studies</Heading2>
        ) : (
          <Heading3 className="mb-6 lg:mb-12">
            Explore Our Case Studies
          </Heading3>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6">
          {realWorldSolutionsCards.map((solution) => (
            <SolutionCard key={solution.id} solution={solution} />
          ))}
        </div>
        {/* CTA Button */}
        <div className="flex items-center justify-center mt-16">
          <ButtonLink
            backgroundColorClass="bg-haizea-blue"
            href="/case-studies"
            className="border border-haizea-blue"
          >
            View All Case Studies
          </ButtonLink>
        </div>
      </div>
    </div>
  );
};

const Stats = ({ solution, className }: StatsProps) => {
  const { className: solutionClassName } = solution;

  // Extract the gradient color for the stat cards
  const getGradientColor = (bgColor: string) => {
    if (bgColor.includes("44b0db"))
      return "from-haizea-blue/20 to-haizea-dark/20";
    if (bgColor.includes("52b773"))
      return "from-haizea-green/20 to-haizea-dark/20";
    if (bgColor.includes("f79c6b"))
      return "from-haizea-orange/20 to-haizea-dark/20";
    return "from-white/10 to-white/5";
  };

  const gradient = getGradientColor(solution.bgColor);

  return (
    <div
      className={cn(
        "grid grid-cols-3 gap-3",
        {
          "lg:grid-cols-1 md:gap-2": !solutionClassName.includes("col-span-2"),
        },
        className
      )}
    >
      {solution.stats?.map((stat, idx) => (
        <div
          key={idx}
          className={`bg-gradient-to-br ${gradient} hover:backdrop-blur-sm border border-white/10 rounded-xl p-3 hover:bg-white/10 transition-all duration-300`}
        >
          <div
            className={cn(
              `text-lg @lg/solution-card:text-2xl @md/solution-card:text-xl font-bold @lg/solution-card:mb-1 text-white`
            )}
          >
            {stat.value}
          </div>
          <div className="text-xs md:text-sm font-medium text-slate-300">
            {stat.label}
          </div>
        </div>
      ))}
    </div>
  );
};
